import { getDisplayUnits, formatVolume } from '../units.js';
import { QuarantineBadge } from './QuarantineBadge.jsx';

export function TankSheet({
  tanks,
  currentId,
  onPick,
  onAdd,
  onSignOut,
  onClose,
  onOpenSettings,
  onOpenAccount,
  onBackToOverview,
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="slide-in"
        style={{
          width: '100%',
          maxWidth: 448,
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          padding: '18px 22px 28px',
          background: 'var(--sheet-bg)',
          borderTop: '1px solid var(--surface-border)',
        }}
      >
        <div
          style={{
            width: 40,
            height: 4,
            borderRadius: 2,
            margin: '0 auto 16px',
            background: 'var(--surface-border)',
          }}
        />
        <h3
          className="font-heading"
          style={{
            fontSize: 15,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            marginBottom: 12,
          }}
        >
          Your tanks
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {onBackToOverview && (
            <button
              onClick={onBackToOverview}
              style={{
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                borderRadius: 12,
                background: 'var(--surface)',
                border: '1px solid var(--surface-border)',
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(125, 211, 255, 0.12)',
                  color: 'var(--cyan)',
                  flexShrink: 0,
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="3" y="3" width="7" height="7" rx="1.5" />
                  <rect x="14" y="3" width="7" height="7" rx="1.5" />
                  <rect x="3" y="14" width="7" height="7" rx="1.5" />
                  <rect x="14" y="14" width="7" height="7" rx="1.5" />
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  className="font-heading"
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: 'var(--text)',
                  }}
                >
                  All tanks
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>At-a-glance overview</div>
              </div>
            </button>
          )}
          {tanks.map((t) => (
            <button
              key={t.id}
              onClick={() => onPick(t.id)}
              style={{
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                borderRadius: 12,
                background: t.id === currentId ? 'var(--accent-bg)' : 'var(--surface)',
                border: `1px solid ${t.id === currentId ? 'var(--accent-border)' : 'var(--surface-border)'}`,
              }}
            >
              {t.has_photo ? (
                <img
                  src={`/api/tanks/${t.id}/photo`}
                  alt=""
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0,0,0,0.15)',
                    fontSize: 24,
                    flexShrink: 0,
                  }}
                >
                  {t.emoji || '🌊'}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="font-heading" style={{ fontSize: 17, fontWeight: 600 }}>
                  {t.name} <QuarantineBadge tank={t} style={{ marginLeft: 4 }} />
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 1 }}>
                  {t.volume_gallons
                    ? formatVolume(t.volume_gallons, getDisplayUnits(t).volume)
                    : ''}
                  {t.volume_gallons && t.type ? ' · ' : ''}
                  {t.type || ''}
                </div>
              </div>
              {t.id === currentId && (
                <span
                  className="font-heading"
                  style={{
                    fontSize: 11,
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    padding: '2px 8px',
                    borderRadius: 999,
                    color: 'var(--cyan-text)',
                    background: 'var(--cyan)',
                  }}
                >
                  Active
                </span>
              )}
              {onOpenSettings && (
                // Settings affordance per-row. Two stopPropagation guards
                // keep the row's own onPick from firing when the gear is
                // tapped — switching tanks vs. opening settings must be
                // unambiguous. role=button on a span (not a nested
                // <button>) because the row itself is already a <button>
                // and HTML forbids interactive nesting.
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`Settings for ${t.name}`}
                  title="Tank settings"
                  className="ts-row-gear"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenSettings(t.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      onOpenSettings(t.id);
                    }
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </span>
              )}
            </button>
          ))}
          {onAdd && (
            <button
              onClick={onAdd}
              style={{
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                borderRadius: 12,
                background: 'transparent',
                border: '1px dashed var(--surface-border)',
                color: 'var(--text-muted)',
              }}
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px dashed var(--text-dim)',
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </div>
              <div className="font-heading" style={{ fontSize: 16, fontWeight: 600 }}>
                Add another tank
              </div>
            </button>
          )}
          {onOpenAccount && (
            <button
              onClick={onOpenAccount}
              className="font-heading"
              style={{
                marginTop: 12,
                width: '100%',
                textAlign: 'left',
                fontSize: 15,
                fontWeight: 600,
                letterSpacing: '0.05em',
                padding: '10px 12px',
                borderRadius: 12,
                background: 'transparent',
                border: '1px dashed var(--surface-border)',
                color: 'var(--text-muted)',
              }}
            >
              Account settings
            </button>
          )}
          {onSignOut && (
            <button
              onClick={onSignOut}
              className="font-heading"
              style={{
                marginTop: 10,
                alignSelf: 'flex-end',
                fontSize: 12,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                padding: '8px 14px',
                borderRadius: 999,
                color: 'var(--text-muted)',
                border: '1px solid var(--surface-border)',
              }}
            >
              Sign out
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
